import { cn } from "@/lib/utils";

// Individual skeleton elements
export const SkeletonText = ({ className, lines = 1 }) => (
  <div className={cn("space-y-2", className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className={cn(
          "skeleton-shimmer rounded h-4",
          i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"
        )}
      />
    ))}
  </div>
);

export const SkeletonAvatar = ({ size = "md", className }) => {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-24 h-24"
  };
  
  return (
    <div className={cn("skeleton-shimmer rounded-full", sizes[size], className)} />
  );
};

export const SkeletonCard = ({ className }) => (
  <div className={cn("skeleton-shimmer skeleton-card", className)} />
);

export const SkeletonButton = ({ className }) => (
  <div className={cn("skeleton-shimmer rounded-xl h-12 w-32", className)} />
);

// Composite skeleton layouts
export const DashboardCardSkeleton = ({ dark = false }) => (
  <div className={cn(
    "rounded-2xl p-6 space-y-4",
    dark ? "bg-stone-800/50" : "bg-white"
  )}>
    <div className="flex items-center justify-between">
      <div className={cn(
        "w-10 h-10 rounded-xl",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
      <div className={cn(
        "w-20 h-6 rounded-full",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
    </div>
    <div className={cn(
      "h-8 w-24 rounded",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
    <div className={cn(
      "h-4 w-full rounded",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
  </div>
);

export const StatCardSkeleton = ({ dark = false }) => (
  <div className={cn(
    "rounded-2xl p-5",
    dark ? "bg-stone-800/50" : "bg-stone-100"
  )}>
    <div className={cn(
      "w-10 h-10 rounded-lg mb-3",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
    <div className={cn(
      "h-8 w-20 rounded mb-2",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
    <div className={cn(
      "h-4 w-24 rounded",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
  </div>
);

export const AppointmentCardSkeleton = ({ dark = false }) => (
  <div className={cn(
    "rounded-xl p-4 flex items-center gap-4",
    dark ? "bg-stone-800/50" : "bg-white border border-stone-200"
  )}>
    <div className={cn(
      "w-14 h-14 rounded-xl",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
    <div className="flex-1 space-y-2">
      <div className={cn(
        "h-5 w-32 rounded",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
      <div className={cn(
        "h-4 w-48 rounded",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
      <div className={cn(
        "h-4 w-24 rounded",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
    </div>
    <div className={cn(
      "w-24 h-10 rounded-lg",
      dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
    )} />
  </div>
);

export const TableRowSkeleton = ({ cols = 4, dark = false }) => (
  <div className="flex items-center gap-4 p-4 border-b border-stone-200">
    {Array.from({ length: cols }).map((_, i) => (
      <div
        key={i}
        className={cn(
          "h-4 rounded flex-1",
          dark ? "skeleton-shimmer-dark" : "skeleton-shimmer",
          i === 0 ? "max-w-[150px]" : ""
        )}
      />
    ))}
  </div>
);

export const ProfileSkeleton = ({ dark = false }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <div className={cn(
        "w-20 h-20 rounded-full",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
      <div className="space-y-2">
        <div className={cn(
          "h-6 w-40 rounded",
          dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
        )} />
        <div className={cn(
          "h-4 w-32 rounded",
          dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
        )} />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      {[1, 2, 3].map(i => (
        <div key={i} className={cn(
          "h-20 rounded-xl",
          dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
        )} />
      ))}
    </div>
  </div>
);

// Full page loading skeleton
export const DashboardSkeleton = ({ dark = false }) => (
  <div className="space-y-6 p-6 animate-fadeInUp">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <div className={cn(
        "h-8 w-48 rounded",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
      <div className={cn(
        "h-10 w-32 rounded-xl",
        dark ? "skeleton-shimmer-dark" : "skeleton-shimmer"
      )} />
    </div>
    
    {/* Stats row */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map(i => (
        <StatCardSkeleton key={i} dark={dark} />
      ))}
    </div>
    
    {/* Main content */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <DashboardCardSkeleton dark={dark} />
      <DashboardCardSkeleton dark={dark} />
    </div>
    
    {/* List */}
    <div className={cn(
      "rounded-2xl overflow-hidden",
      dark ? "bg-stone-800/50" : "bg-white"
    )}>
      {[1, 2, 3].map(i => (
        <AppointmentCardSkeleton key={i} dark={dark} />
      ))}
    </div>
  </div>
);

export default DashboardSkeleton;
